/**
 * Who is signed in on this device.
 *
 * The session cookie is httpOnly, so the browser cannot read it — the server is the
 * only source of truth. Asking on every visit to a teacher screen is also what keeps
 * the 30-day session rolling forward (docs/02-SRS.md B2).
 */

import { useEffect, useState } from 'react';

import type { Role } from '@opal/shared';

export interface SignedInTeacher {
  readonly email: string;
  readonly role: Role;
}

export type SessionState =
  | { readonly status: 'checking' }
  | { readonly status: 'signed-in'; readonly teacher: SignedInTeacher }
  | { readonly status: 'signed-out' };

const ROLES: readonly Role[] = ['teacher', 'student', 'admin'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Reads the signed-in teacher out of a session response. Anything unexpected is a no. */
export function readTeacher(body: unknown): SignedInTeacher | null {
  if (!isRecord(body) || body.authenticated !== true || !isRecord(body.teacher)) return null;
  const { email, role } = body.teacher;
  const knownRole = ROLES.find((candidate) => candidate === role);
  if (typeof email !== 'string' || email === '' || !knownRole) return null;
  return { email, role: knownRole };
}

async function fetchSession(): Promise<SignedInTeacher | null> {
  try {
    const response = await fetch('/api/auth/session', { credentials: 'same-origin' });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    return readTeacher(body);
  } catch {
    // Server unreachable counts as signed out: the sign-in screen is the safe landing.
    return null;
  }
}

/** Asks the server who is signed in. Returns 'checking' until the answer arrives. */
export function useTeacherSession(): SessionState {
  const [state, setState] = useState<SessionState>({ status: 'checking' });

  useEffect(() => {
    let cancelled = false;
    void fetchSession().then((teacher) => {
      if (cancelled) return;
      setState(teacher ? { status: 'signed-in', teacher } : { status: 'signed-out' });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

/** Ends the session on the server. The caller decides where to go next. */
export async function signOut(): Promise<void> {
  try {
    await fetch('/api/auth/sign-out', { method: 'POST', credentials: 'same-origin' });
  } catch {
    // The cookie expires on its own; a failed sign-out must not trap the teacher here.
  }
}
